import { SvgIconComponent } from "@mui/icons-material";
import { Error, Schedule } from "@mui/icons-material";
import dayjs from "dayjs";
import { FormikProps } from "formik";

import { Layer } from "@/Utils/hierarchy";

import { SUPER_OPTIMIZE_SCENARIO_COUNT } from "./constants";
import { StrategyProfile } from "@mutiny-group/sbo-openapi-client";
import { lowerCase } from "lodash";

const SUPER_OPTIMIZER_LABEL = "Super Optimizer";

export enum ScenarioTypeEnum {
  Unknown = "unknown",
  Budget = "budget",
  Revenue = "revenue",
  Optimizer = "optimizer",
}

export enum ScenarioFilterTypeEnum {
  Forecast = "forecast",
  Optimizer = "optimizer",
  SuperOptimizer = "superOptimizer",
}

export class ScenarioType {
  static isSuperOptimizer(scenarioType: ScenarioTypeEnum, scenarioCount?: number): boolean {
    return (
      scenarioType === ScenarioTypeEnum.Optimizer &&
      typeof scenarioCount === "number" &&
      scenarioCount >= SUPER_OPTIMIZE_SCENARIO_COUNT
    );
  }

  static toString(scenarioType: ScenarioTypeEnum, scenarioCount?: number): string {
    if (ScenarioType.isSuperOptimizer(scenarioType, scenarioCount)) {
      return SUPER_OPTIMIZER_LABEL;
    }

    const scenarioTypeMap: Record<ScenarioTypeEnum, string> = {
      [ScenarioTypeEnum.Unknown]: "Unknown",
      [ScenarioTypeEnum.Budget]: "Forecast",
      [ScenarioTypeEnum.Optimizer]: "Optimizer",
      [ScenarioTypeEnum.Revenue]: "Revenue",
    };

    return scenarioTypeMap[scenarioType] || ScenarioType.toString(ScenarioTypeEnum.Unknown);
  }

  /**
   * Returns the display name for a scenario type, with special handling for V2 mode
   * @param scenarioType The scenario type enum
   * @param scenarioCount The scenario count (used to determine super optimizer)
   * @param isOptimizerV2Enabled Whether V2 mode is enabled
   */
  static getDisplayName(
    scenarioType: ScenarioTypeEnum,
    scenarioCount?: number,
    isOptimizerV2Enabled?: boolean
  ): string {
    // In V2 mode, we never show "Super Optimizer"
    if (isOptimizerV2Enabled && ScenarioType.isSuperOptimizer(scenarioType, scenarioCount)) {
      return "Optimizer";
    }

    // Use the normal toString method for other cases
    return ScenarioType.toString(scenarioType, scenarioCount);
  }

  //
  static fromString(str: string): ScenarioTypeEnum {
    const scenarioTypeMap: Record<string, ScenarioTypeEnum> = {
      unknown: ScenarioTypeEnum.Unknown,
      budget: ScenarioTypeEnum.Budget,
      optimizer: ScenarioTypeEnum.Optimizer,
      revenue: ScenarioTypeEnum.Revenue,
    };

    return scenarioTypeMap[str] || ScenarioTypeEnum.Unknown;
  }

  static getStyle(type: ScenarioTypeEnum, theme: any, scenarioCount?: number) {
    if (ScenarioType.isSuperOptimizer(type, scenarioCount)) {
      return {
        labelColor: theme.palette.scenarioType.superOptimizer,
      };
    }

    const styleMap: Record<ScenarioTypeEnum, { labelColor: string }> = {
      [ScenarioTypeEnum.Optimizer]: {
        labelColor: theme.palette.scenarioType.optimizer,
      },
      [ScenarioTypeEnum.Revenue]: {
        labelColor: theme.palette.scenarioType.revenue,
      },
      [ScenarioTypeEnum.Budget]: {
        labelColor: theme.palette.scenarioType.forecast,
      },
      [ScenarioTypeEnum.Unknown]: {
        labelColor: theme.palette.scenarioType.forecast, // fallback color
      },
    };

    return styleMap[type] || styleMap[ScenarioTypeEnum.Unknown];
  }
}

export enum StatusEnum {
  Unknown = "unknown",
  Completed = "completed",
  Processing = "processing",
  Draft = "draft",
  Error = "error",
  Approved = "approved",
  Archived = "archived",
}

export enum ActivityEventEnum {
  ScenarioCreated = "ScenarioCreated",
  ScenarioStatusUpdated = "ScenarioStatusUpdated",
  ScenarioDuplicated = "ScenarioDuplicated",
  ScenarioSubmitted = "ScenarioRun",
  ScenarioCommentAdded = "ScenarioCommentAdded",
}

export interface StatusStyle {
  color: string;
  icon?: SvgIconComponent;
}

export class Status {
  static toString(status: StatusEnum, isScenarioBuilderApprovalsEnabled: boolean = false): string {
    const statusMap: Record<StatusEnum, string> = {
      [StatusEnum.Completed]: isScenarioBuilderApprovalsEnabled ? "Ready for Review" : "Completed",
      [StatusEnum.Processing]: "Simulating",
      [StatusEnum.Draft]: "Draft",
      [StatusEnum.Error]: "Error",
      [StatusEnum.Unknown]: "Unknown",
      [StatusEnum.Approved]: "Approved",
      [StatusEnum.Archived]: "Archived",
    };

    return statusMap[status] || Status.toString(StatusEnum.Unknown);
  }

  static fromString(str: string): StatusEnum {
    const statusMap: Record<string, StatusEnum> = {
      completed: StatusEnum.Completed,
      processing: StatusEnum.Processing,
      draft: StatusEnum.Draft,
      error: StatusEnum.Error,
      approved: StatusEnum.Approved,
      archived: StatusEnum.Archived,
    };

    return statusMap[str] || StatusEnum.Unknown;
  }

  static getStyle(status: StatusEnum, theme: any): StatusStyle {
    const styleMap: Record<StatusEnum, StatusStyle> = {
      [StatusEnum.Approved]: {
        color: theme.palette.statusColors.approved,
      },
      [StatusEnum.Archived]: {
        color: theme.palette.statusColors.archived,
      },
      [StatusEnum.Draft]: {
        color: theme.palette.statusColors.draft,
      },
      [StatusEnum.Completed]: {
        color: theme.palette.statusColors.completed,
      },
      [StatusEnum.Processing]: {
        color: theme.palette.statusColors.processing,
        icon: Schedule,
      },
      [StatusEnum.Error]: {
        color: theme.palette.statusColors.error,
        icon: Error,
      },
      [StatusEnum.Unknown]: {
        color: theme.palette.statusColors.unknown,
      },
    };

    return styleMap[status] || styleMap[StatusEnum.Unknown];
  }
}

export type Scenario = {
  uuid: string;
  name: string;
  brand: string;
  spend: number;
  revenue: number;
  roi: number;
  targetNodeUUID: string;
  startDate: Date;
  endDate: Date;
  createdBy: string;
  createdAt: Date;
  status: StatusEnum;
  statusMessage: string;
  type: ScenarioTypeEnum;
  scenarioCount: number;
  geography: string | null;
  isEligibleForActionPlans?: boolean;
};

export type ScenarioSpend = {
  marketingSpendNodeUuid: string;
  channel: string;
  spend: number;
};

export type ScenarioRevenue = {
  saleNodeUuid: string;
  marketingSpendNodeUuid: string;
  channel: string;
  revenue: number;
};

export type ScenarioReport = {
  confidence: number | null;
  scenarioRevenues: ScenarioRevenue[];
  scenarioSpends: ScenarioSpend[];
};

export type MediaConstraint = {
  channel: string;
  level: Layer;
  nodeUUID: string;
  minValue: number | null;
  // TODO: Rename this to maxValue after optimizer is released
  value: number | null;
};

export type ScenarioDateRange = {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
};

export interface ScenarioFormValues {
  uuid: string;
  name: string;
  description: string | null;
  scenarioTarget: string | null;
  geography?: string;
  mediaConstraints: MediaConstraint[];
  totalBudget: number | null;
  totalBrandMarketingBudgetDollar: null;
  totalCategoryMarketingBudgetDollar: null;
  totalProductMarketingBudgetDollar: null;
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  // Workaround to make sure formik is initialised on first render
  isInitialised: boolean;
  type: ScenarioTypeEnum;
}

export type ScenarioFormikProps = FormikProps<ScenarioFormValues>;

export enum MenuItemType {
  Rename = "Rename",
  ShareLink = "Share Link",
  Duplicate = "Duplicate",
  Delete = "Delete",
}

export enum ModalType {
  Delete = MenuItemType.Delete,
  Duplicate = MenuItemType.Duplicate,
}

export enum SpendBreakdownEnum {
  ROI = "roi",
  SPEND_DOLLARS = "spendDollars",
  SPEND_PERCENTAGE = "spendPercentage",
}

export enum ToolTipType {
  REVENUE = "revenue",
  SPEND = "spend",
  ROI = "roi",
}

export type LikelihoodConfig = {
  strategyProfile: StrategyProfile;
  label: string;
  description: string;
  style: {
    backgroundColor: string;
    color?: string;
  };
};

export const RISK_SMALL_STYLE: { backgroundColor: string; color?: string } = {
  backgroundColor: "rgba(86, 94, 250, 0.15)",
};

export const RISK_MEDIUM_STYLE: { backgroundColor: string; color?: string } = {
  backgroundColor: "rgba(86, 94, 250, 0.4)",
};

export const RISK_LARGE_STYLE: { backgroundColor: string; color?: string } = {
  backgroundColor: "#565EFA",
  color: "#FFFFFF",
};

export const LikelihoodMapping: LikelihoodConfig[] = [
  {
    strategyProfile: StrategyProfile.Conservative,
    label: "small",
    description: "are likely to be close to predictions",
    style: RISK_SMALL_STYLE,
  },
  {
    strategyProfile: StrategyProfile.Moderate,
    label: "medium",
    description: "may differ somewhat from predictions",
    style: RISK_MEDIUM_STYLE,
  },
  {
    strategyProfile: StrategyProfile.Aggressive,
    label: "large",
    description: "may differ substantially from predictions",
    style: RISK_LARGE_STYLE,
  },
];

export const getLikelihood = (strategyProfile: StrategyProfile) => {
  return (
    LikelihoodMapping.find(
      (config) => lowerCase(config.strategyProfile) === lowerCase(strategyProfile)
    ) ?? null
  );
};
